<template>
  <div class="px-3 py-3 pb-10 space-y-4 max-w-screen-2xl mx-auto">
    <!-- Header -->
    <div class="flex items-center justify-between gap-3">
      <div class="min-w-0">
        <h1 class="text-lg font-bold leading-tight text-gray-900 dark:text-gray-50">IT Support</h1>
        <p class="text-xs text-gray-500 dark:text-gray-400">Track hardware, software, network and access requests.</p>
      </div>
      <button type="button" @click="openCreate"
        class="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-amber-400 px-3.5 py-2 text-sm font-semibold text-gray-900 shadow-sm transition active:scale-95">
        <v-icon size="18">mdi-plus</v-icon>New Ticket
      </button>
    </div>

    <!-- Filters -->
    <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
      <div class="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <v-select density="compact" variant="solo-filled" flat hide-details clearable label="Status" :items="statusItems" v-model="filters.status" @update:modelValue="fetchTickets"></v-select>
        <v-select density="compact" variant="solo-filled" flat hide-details clearable label="Category" :items="categoryItems" v-model="filters.category" @update:modelValue="fetchTickets"></v-select>
        <v-autocomplete density="compact" variant="solo-filled" flat hide-details clearable label="Assignee" :items="assigneeItems" item-title="title" item-value="value" v-model="filters.assignee_id" @update:modelValue="fetchTickets"></v-autocomplete>
        <v-text-field density="compact" variant="solo-filled" flat hide-details placeholder="Search…" prepend-inner-icon="mdi-magnify" v-model="search"></v-text-field>
      </div>
      <div class="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
        <v-text-field density="compact" variant="solo-filled" flat hide-details clearable type="date" label="Created from" v-model="dateFrom"></v-text-field>
        <v-text-field density="compact" variant="solo-filled" flat hide-details clearable type="date" label="Created to" v-model="dateTo"></v-text-field>
        <button type="button" @click="overdueOnly = !overdueOnly"
          class="inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition active:scale-95"
          :class="overdueOnly ? 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'">
          <v-icon size="16">mdi-alert-circle-outline</v-icon> Overdue only
        </button>
      </div>
    </div>

    <!-- Status summary -->
    <div class="grid grid-cols-2 gap-2 sm:grid-cols-5">
      <StatCard v-for="s in statusItems" :key="s" :label="s" :value="countByStatus(s)" :color="statusColor(s)" />
      <StatCard label="Overdue" :value="overdueCount" color="error" />
    </div>

    <!-- Ticket list -->
    <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
      <!-- Loading -->
      <div v-if="loading && !tickets.length" class="space-y-2">
        <div v-for="n in 6" :key="'il-' + n" class="h-24 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800"></div>
      </div>

      <!-- Empty -->
      <EmptyState v-else-if="!displayTickets.length" icon="mdi-lifebuoy" title="No IT tickets yet." dense />

      <!-- Cards (search reuses the existing filterRow method) -->
      <div v-else class="space-y-2">
        <div v-for="item in displayTickets" :key="item.id" v-show="filterRow(null, search, { raw: item })"
          class="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900">
          <!-- Header: ref + overdue / priority + status menu -->
          <div class="flex items-center justify-between gap-2">
            <div class="flex min-w-0 items-center gap-2">
              <span class="font-mono text-xs font-bold text-gray-500 dark:text-gray-400">{{ item.ref }}</span>
              <span v-if="isOverdue(item)" class="rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">Overdue</span>
            </div>
            <div class="flex shrink-0 items-center gap-1.5">
              <PriorityChip :priority="item.priority" />
              <v-menu>
                <template v-slot:activator="{ props }">
                  <span v-bind="props" class="cursor-pointer"><StatusChip :status="item.status" size="x-small" /></span>
                </template>
                <v-list density="compact">
                  <v-list-item v-for="s in statusItems" :key="s" :title="s" @click="changeStatus(item, s)"></v-list-item>
                </v-list>
              </v-menu>
            </div>
          </div>

          <!-- Title -->
          <p class="mt-1.5 text-sm font-semibold leading-snug text-gray-900 dark:text-gray-50">{{ item.title }}</p>

          <!-- Meta: category · requester · due -->
          <div class="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
            <span class="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 dark:bg-slate-500/15 dark:text-slate-300">{{ item.category }}</span>
            <span v-if="item.requester" class="text-gray-500 dark:text-gray-400">{{ item.requester }}</span>
            <span v-if="item.due_date" class="inline-flex items-center gap-1"
              :class="isOverdue(item) ? 'font-semibold text-rose-600 dark:text-rose-400' : 'text-gray-500 dark:text-gray-400'">
              <v-icon size="13">mdi-calendar-clock</v-icon>{{ formattedDate(item.due_date) }}
            </span>
          </div>

          <!-- Footer: assignee + actions -->
          <div class="mt-2.5 flex items-center justify-between gap-2 border-t border-gray-100 pt-2 dark:border-gray-800">
            <div class="flex min-w-0 items-center gap-2">
              <span v-if="item.assignee_name" class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-100 text-[10px] font-bold text-amber-800 dark:bg-amber-500/20 dark:text-amber-300">{{ initials(item.assignee_name) }}</span>
              <span v-else class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-400 dark:bg-gray-800"><v-icon size="14">mdi-account-outline</v-icon></span>
              <span class="truncate text-xs text-gray-600 dark:text-gray-300">{{ item.assignee_name || 'Unassigned' }}</span>
            </div>
            <div class="flex shrink-0 items-center gap-1">
              <button type="button" aria-label="Edit ticket" @click="openEdit(item)"
                class="rounded-lg p-1.5 text-gray-500 transition hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"><v-icon size="18">mdi-pencil-outline</v-icon></button>
              <button type="button" aria-label="Transfer ticket" @click="openTransfer(item)"
                class="rounded-lg p-1.5 text-gray-500 transition hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"><v-icon size="18">mdi-account-switch-outline</v-icon></button>
              <button type="button" aria-label="Delete ticket" @click="askDelete(item)"
                class="rounded-lg p-1.5 text-rose-500 transition hover:bg-rose-50 dark:hover:bg-rose-500/10"><v-icon size="18">mdi-delete-outline</v-icon></button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create / edit dialog -->
    <v-dialog v-model="dialog" max-width="640">
      <v-card :title="editing ? `Edit ${form.ref}` : 'New IT Ticket'" prepend-icon="mdi-lifebuoy">
        <v-card-text>
          <v-text-field density="compact" label="Title*" v-model="form.title"></v-text-field>
          <v-textarea density="compact" label="Description" rows="3" v-model="form.description"></v-textarea>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-select density="compact" label="Category" :items="categoryItems" v-model="form.category"></v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select density="compact" label="Priority" :items="priorityItems" v-model="form.priority"></v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field density="compact" label="Requester" v-model="form.requester"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-autocomplete density="compact" label="Assignee" :items="assigneeItems" item-title="title" item-value="value" v-model="form.assignee_id" clearable></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field density="compact" type="date" label="Due date*" prepend-inner-icon="mdi-calendar-clock" v-model="form.due_date"></v-text-field>
            </v-col>
            <v-col cols="12" v-if="editing">
              <v-textarea density="compact" label="Resolution notes" rows="2" v-model="form.resolution"></v-textarea>
            </v-col>
          </v-row>

          <template v-if="editing">
            <v-divider class="my-3"></v-divider>
            <div class="text-caption text-grey mb-2">
              Created {{ formattedDateTime(form.created_at) }}<span v-if="form.created_by"> by {{ form.created_by }}</span>
              · Updated {{ formattedDateTime(form.updated_at) }}<span v-if="form.updated_by"> by {{ form.updated_by }}</span>
            </div>

            <div class="int-label">Activity &amp; comments</div>
            <div class="d-flex align-center mb-3" style="gap: 8px">
              <v-text-field density="compact" hide-details placeholder="Add a comment…" v-model="commentText" @keyup.enter="addComment"></v-text-field>
              <v-btn color="primary" variant="tonal" :disabled="!commentText" @click="addComment">Post</v-btn>
            </div>
            <div v-if="activityLoading" class="text-center py-3">
              <v-progress-circular indeterminate size="22"></v-progress-circular>
            </div>
            <v-timeline v-else-if="activity.length" density="compact" side="end" class="int-timeline">
              <v-timeline-item v-for="a in activity" :key="a.id" :dot-color="activityColor(a.action)" size="x-small">
                <div class="d-flex justify-space-between" style="gap: 12px">
                  <div>
                    <span class="font-weight-medium">{{ a.action === 'comment' ? a.content : a.message }}</span>
                    <div class="text-caption text-grey">{{ a.actor_name }}</div>
                  </div>
                  <div class="text-caption text-grey text-no-wrap">{{ formattedDateTime(a.created_at) }}</div>
                </div>
              </v-timeline-item>
            </v-timeline>
            <EmptyState v-else icon="mdi-timeline-text-outline" title="No activity yet." dense />
          </template>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text="Close" variant="plain" @click="dialog = false"></v-btn>
          <v-btn color="primary" text="Save" variant="flat" :disabled="!form.title || !form.due_date" @click="save"></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirmation -->
    <v-dialog v-model="deleteDialog" max-width="420">
      <v-card prepend-icon="mdi-delete-alert-outline" title="Delete IT ticket?">
        <v-card-text>
          This will remove <span class="font-weight-medium">{{ deleteTarget ? deleteTarget.ref : '' }}</span><span v-if="deleteTarget && deleteTarget.title"> — “{{ deleteTarget.title }}”</span>. This can’t be undone.
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text="Cancel" variant="plain" :disabled="deleting" @click="deleteDialog = false"></v-btn>
          <v-btn color="error" variant="flat" :loading="deleting" @click="confirmDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <TransferDialog
      v-model="transferDialog"
      :item="transferTarget"
      :endpoint="constant.it_transfer"
      id-key="id"
      entity-label="IT ticket"
      :title="transferTarget ? transferTarget.ref : ''"
      :employees="employees"
      :current-assignee-id="transferTarget ? transferTarget.assignee_id : null"
      :current-name="transferTarget ? transferTarget.assignee_name : ''"
      :updated-by="currentUserLabel()"
      @transferred="onTransferred"
    ></TransferDialog>

    <Snackbar :toggle-snackbar="isshow" :response-message="message"></Snackbar>
  </div>
</template>

<script>
import axios from 'axios';
import utils from '@/utils';
import constant from '@/constant';
import Snackbar from './Snackbar.vue';
import apiMixin from '@/mixins/apiMixin';
import formatMixin from '@/mixins/formatMixin';
import StatCard from '@/components/ui/StatCard.vue';
import StatusChip from '@/components/ui/StatusChip.vue';
import PriorityChip from '@/components/ui/PriorityChip.vue';
import EmptyState from '@/components/ui/EmptyState.vue';
import TransferDialog from './TransferDialog.vue';

export default {
  name: 'ItSupportPage',
  components: { Snackbar, StatCard, StatusChip, PriorityChip, EmptyState, TransferDialog },
  mixins: [apiMixin, formatMixin],
  data() {
    return {
      constant: constant,
      loading: false,
      search: '',
      dateFrom: null,
      dateTo: null,
      overdueOnly: false,
      tickets: [],
      employees: [],
      user_data: null,
      isshow: false,
      message: '',
      dialog: false,
      editing: false,
      deleteDialog: false,
      deleteTarget: null,
      deleting: false,
      transferDialog: false,
      transferTarget: null,
      activity: [],
      activityLoading: false,
      commentText: '',
      filters: { status: null, category: null, assignee_id: null },
      form: this.blankForm(),
      statusItems: ['Open', 'In Progress', 'Resolved', 'Closed'],
      categoryItems: ['Hardware', 'Software', 'Network', 'Access', 'Other'],
      priorityItems: ['Low', 'Medium', 'High', 'Urgent'],
      headers: [
        { title: 'Ref', key: 'ref', sortable: true },
        { title: 'Title', key: 'title', sortable: true },
        { title: 'Category', key: 'category', sortable: true },
        { title: 'Priority', key: 'priority', sortable: true },
        { title: 'Requester', key: 'requester', sortable: true },
        { title: 'Assignee', key: 'assignee_name', sortable: false },
        { title: 'Status', key: 'status', sortable: true },
        { title: 'Due', key: 'due_date', sortable: true },
        { title: 'Created by', key: 'created_by', sortable: true },
        { title: 'Created', key: 'created_at', sortable: true },
        { title: 'Updated by', key: 'updated_by', sortable: true },
        { title: 'Updated', key: 'updated_at', sortable: true },
        { title: '', key: 'actions', sortable: false, align: 'end' },
      ],
    };
  },
  computed: {
    assigneeItems() {
      return [{ value: null, title: 'All' }, ...this.employees.map(e => ({ value: e.id, title: e.fullname }))];
    },
    // Today as YYYY-MM-DD (local) for overdue comparison.
    today() {
      const d = new Date();
      const off = d.getTimezoneOffset();
      return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
    },
    // Apply the From/To created-date range and overdue toggle on top of the
    // server-side filters.
    displayTickets() {
      return this.tickets.filter((t) => {
        if (this.dateFrom || this.dateTo) {
          if (!t.created_at) return false;
          const d = String(t.created_at).slice(0, 10);
          if (this.dateFrom && d < this.dateFrom) return false;
          if (this.dateTo && d > this.dateTo) return false;
        }
        if (this.overdueOnly && !this.isOverdue(t)) return false;
        return true;
      });
    },
    overdueCount() {
      return this.tickets.filter(t => this.isOverdue(t)).length;
    },
  },
  methods: {
    blankForm() {
      return { id: null, ref: '', title: '', description: '', category: 'Other', priority: 'Medium', requester: '', assignee_id: null, due_date: null, resolution: '', created_by: null, updated_by: null, created_at: null, updated_at: null };
    },
    // Overdue = has a due date strictly before today and not yet finished
    // (Resolved/Closed never count).
    isOverdue(item) {
      if (!item || !item.due_date) return false;
      if (item.status === 'Resolved' || item.status === 'Closed') return false;
      return String(item.due_date).slice(0, 10) < this.today;
    },
    activityColor(action) {
      switch (action) {
        case 'created': return 'primary';
        case 'status': return 'info';
        case 'assignee': return 'warning';
        case 'comment': return 'success';
        default: return 'grey';
      }
    },
    countByStatus(s) {
      return this.displayTickets.filter(t => t.status === s).length;
    },
    filterRow(value, query, item) {
      if (!query) return true;
      const q = query.toLowerCase();
      const raw = item && item.raw ? item.raw : {};
      const hay = [raw.ref, raw.title, raw.requester, raw.assignee_name, raw.category].join(' ').toLowerCase();
      return hay.includes(q);
    },
    notify(msg) {
      this.isshow = true;
      this.message = msg;
      setTimeout(() => { this.isshow = false; }, 1500);
    },
    buildParams() {
      const p = {};
      if (this.filters.status) p.status = this.filters.status;
      if (this.filters.category) p.category = this.filters.category;
      if (this.filters.assignee_id) p.assignee_id = this.filters.assignee_id;
      return p;
    },
    fetchTickets() {
      this.loading = true;
      axios.get(utils._api(this.constant.it_list), { ...this.authHeaders(), params: this.buildParams() })
        .then((response) => { this.tickets = response.data.data || []; })
        .catch((error) => { console.error('Error fetching IT tickets:', error); this.notify('Failed to load IT tickets'); })
        .finally(() => { this.loading = false; });
    },
    getEmployees() {
      axios.get(utils._api(this.constant.get_active_users), this.authHeaders())
        .then((response) => { this.employees = response.data.data || []; })
        .catch(error => console.error('Error fetching users:', error));
    },
    openCreate() {
      this.editing = false;
      this.form = this.blankForm();
      this.form.requester = this.currentUserLabel();
      this.dialog = true;
    },
    openEdit(item) {
      this.editing = true;
      this.form = {
        id: item.id,
        ref: item.ref,
        title: item.title,
        description: item.description,
        category: item.category,
        priority: item.priority,
        requester: item.requester,
        assignee_id: item.assignee_id,
        due_date: item.due_date ? String(item.due_date).slice(0, 10) : null,
        resolution: item.resolution || '',
        created_by: item.created_by,
        updated_by: item.updated_by,
        created_at: item.created_at,
        updated_at: item.updated_at,
      };
      this.commentText = '';
      this.activity = [];
      this.dialog = true;
      this.loadActivity(item.id);
    },
    loadActivity(id) {
      this.activityLoading = true;
      axios.post(utils._api(this.constant.it_activity), { id }, this.authHeaders())
        .then((response) => { this.activity = response.data.data || []; })
        .catch((error) => { console.error('Error fetching activity:', error); })
        .finally(() => { this.activityLoading = false; });
    },
    addComment() {
      if (!this.commentText || !this.form.id) return;
      axios.post(utils._api(this.constant.it_add_comment), {
        id: this.form.id,
        content: this.commentText,
        user: this.currentUserLabel(),
      }, this.authHeaders())
        .then(() => {
          this.commentText = '';
          this.loadActivity(this.form.id);
        })
        .catch((error) => { console.error('Error adding comment:', error); this.notify('Failed to add comment'); });
    },
    save() {
      const url = this.editing ? this.constant.it_update : this.constant.it_create;
      const payload = this.editing ? { ...this.form, updated_by: this.currentUserLabel() } : {
        title: this.form.title,
        description: this.form.description,
        category: this.form.category,
        priority: this.form.priority,
        requester: this.form.requester,
        assignee_id: this.form.assignee_id,
        due_date: this.form.due_date,
      };
      axios.post(utils._api(url), payload, this.authHeaders())
        .then((response) => {
          this.dialog = false;
          this.notify(response.data.message);
          this.fetchTickets();
        })
        .catch((error) => { console.error('Error saving IT ticket:', error); this.notify('Failed to save'); });
    },
    openTransfer(item) {
      this.transferTarget = item;
      this.transferDialog = true;
    },
    onTransferred(item, errorMsg) {
      if (errorMsg) { this.notify(errorMsg); return; }
      this.notify('Ticket transferred');
      this.fetchTickets();
    },
    askDelete(item) {
      this.deleteTarget = item;
      this.deleteDialog = true;
    },
    confirmDelete() {
      if (!this.deleteTarget) return;
      this.deleting = true;
      axios.post(utils._api(this.constant.it_delete), { id: this.deleteTarget.id }, this.authHeaders())
        .then((response) => {
          this.deleteDialog = false;
          this.deleteTarget = null;
          this.notify(response.data.message || 'IT ticket deleted');
          this.fetchTickets();
        })
        .catch((error) => { console.error('Error deleting IT ticket:', error); this.notify('Failed to delete'); })
        .finally(() => { this.deleting = false; });
    },
    changeStatus(item, status) {
      if (item.status === status) return;
      axios.post(utils._api(this.constant.it_update_status), {
        id: item.id,
        status: status,
        updated_by: this.currentUserLabel(),
      }, this.authHeaders())
        .then((response) => { this.notify(response.data.message); this.fetchTickets(); })
        .catch((error) => { console.error('Error updating status:', error); this.notify('Failed to update status'); });
    },
  },
  mounted() {
    const userData = localStorage.getItem('user_data');
    if (userData) {
      try { this.user_data = JSON.parse(userData); } catch (e) { this.user_data = null; }
    }
    this.getEmployees();
    this.fetchTickets();
  },
};
</script>

<style scoped>
.int-it-table :deep(th) {
  font-weight: 700 !important;
  color: #3a3b40 !important;
}
.int-it-table :deep(td) {
  font-size: 0.85rem;
}
.int-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8a8b90;
  margin-bottom: 6px;
}
.int-timeline {
  max-height: 260px;
  overflow-y: auto;
}
</style>
